/**
 * Centralized tier type and helpers.
 *
 * Business rules:
 * - A rooftop is either 'lite' or 'standard' (stored on customers.tier)
 * - A user can have access to rooftops of mixed tiers
 * - A user's "effective tiers" = the set of tiers from their linked rooftops
 */

export type Tier = 'lite' | 'standard';

/** All known tier values for type guards */
const TIER_VALUES = new Set<string>(['lite', 'standard']);

export function isLiteTier(tier: string | null | undefined): boolean {
  return tier === 'lite';
}

export function isStandardTier(tier: string | null | undefined): boolean {
  return tier === 'standard';
}

export function isValidTier(tier: string | null | undefined): tier is Tier {
  return !!tier && TIER_VALUES.has(tier);
}

// ── Object-level helpers ──

/** Check if a rooftop (customer) is lite tier */
export function isRooftopLite(rooftop: { tier?: string | null } | null | undefined): boolean {
  return isLiteTier(rooftop?.tier);
}

/** Rooftop shape needed to decide driver capability (tier + service flags). */
export interface DriverCapableRooftop {
  tier?: string | null;
  effective_config?: Record<string, any> | null;
}

/**
 * A rooftop may manage its OWN drivers when it is `lite` (self-managed by definition)
 * OR has the Mobile Service service enabled. The single source of truth for the "driver
 * capability" gate.
 *
 * Reads `mobile_service.active` off the rooftop row, not the portal products, because user
 * management evaluates every rooftop a user is assigned to and the portal-products snapshot
 * covers only the working rooftop.
 */
export function canHaveDrivers(rooftop: DriverCapableRooftop | null | undefined): boolean {
  if (!rooftop) return false;
  return isLiteTier(rooftop.tier) || rooftop.effective_config?.mobile_service?.active === true;
}

/** Check if a move is lite (has the lite boolean flag set) */
export function isMoveLite(move: { lite?: boolean | null } | null | undefined): boolean {
  return !!move?.lite;
}

// ── User tier context ──

export interface UserTierInfo {
  /** All tiers this user has access to */
  tiers: Set<Tier>;
  /** Has at least one lite rooftop */
  hasLite: boolean;
  /** Has at least one standard rooftop */
  hasStandard: boolean;
  /** Has both lite and standard rooftops */
  isMixed: boolean;
  /** Only has lite rooftops (no standard) */
  isLiteOnly: boolean;
  /** Only has standard rooftops (no lite) */
  isStandardOnly: boolean;
}

/**
 * Derive tier info from a user's linked customers.
 * Accepts any array of objects with a customer that has a tier field.
 *
 * A named exception that stays on `customers.tier`: this spans every rooftop a user is linked
 * to, while the product-access snapshot (the resolved operational model) covers only the
 * working rooftop, so there is no resolved model to read for the user's other rooftops.
 * `useUserCapabilities`, `rooftopChangeResolver`, and `UserCustomersTab` inherit this.
 */
export function getUserTierInfo(
  usertocustomers: Array<{ customer?: { tier?: string | null } | null }> | null | undefined,
): UserTierInfo {
  const tiers = new Set<Tier>();

  if (usertocustomers) {
    for (const utc of usertocustomers) {
      const t = utc.customer?.tier;
      if (t && isValidTier(t)) {
        tiers.add(t);
      }
    }
  }

  const hasLite = tiers.has('lite');
  const hasStandard = tiers.has('standard');

  return {
    tiers,
    hasLite,
    hasStandard,
    isMixed: hasLite && hasStandard,
    isLiteOnly: hasLite && !hasStandard,
    isStandardOnly: hasStandard && !hasLite,
  };
}
